/**
 * Quick task 260810-l18 — Approve / Reopen panel for the AUTHOR of a finished
 * ticket. Shared by the portal (external audience, RequestDetail.tsx) and the
 * internal task detail slide-over (audience: 'internal', Task 3).
 *
 * Approve submits immediately with no reason (confirmation that the ticket is
 * really fixed); Reopen requires a reason (required-reason textarea gates the
 * submit button — sends the ticket back). The backend re-checks both the
 * author gate and the terminal gate, so any 4xx surfaces as a toast rather
 * than a client-side dead end; a failed mutation leaves the panel open.
 */
import { useState, useCallback } from 'react';
import { Button } from '../common/Button';
import { useAuthorReview } from '../../hooks/useAuthorReview';
import { useToast } from '../ui/Toast';
import type { AuthorReviewAudience } from '../../hooks/useAuthorReview';

export interface AuthorReviewActionsProps {
  taskId: number;
  projectId: number;
  audience: AuthorReviewAudience;
}

const REASON_MAX = 2000;

export function AuthorReviewActions({ taskId, projectId, audience }: AuthorReviewActionsProps) {
  const review = useAuthorReview();
  const showToast = useToast();
  const [reopening, setReopening] = useState(false);
  const [reason, setReason] = useState('');

  const submit = useCallback(
    (decision: 'approved' | 'reopened', reasonText?: string) => {
      review.mutate(
        { taskId, projectId, audience, decision, reason: reasonText },
        {
          onError: (err) => {
            const msg = err instanceof Error ? err.message : 'Submission failed';
            showToast(msg, 'error');
          },
          onSuccess: () => {
            showToast(decision === 'reopened' ? 'Ticket reopened' : 'Approved', 'success');
            setReopening(false);
            setReason('');
          },
        },
      );
    },
    [review, taskId, projectId, audience, showToast],
  );

  const reasonTrimmed = reason.trim();
  const canSubmitReopen =
    reasonTrimmed.length > 0 && reasonTrimmed.length <= REASON_MAX && !review.isPending;

  return (
    <div
      className="mt-4 rounded-lg border border-ms-border bg-ms-canvas p-3"
      data-testid="author-review-actions"
    >
      <div className="text-sm font-medium text-ms-text-strong mb-2">Your ticket</div>
      {!reopening ? (
        <div className="flex items-center gap-2">
          <Button
            variant="primary"
            onClick={() => submit('approved')}
            disabled={review.isPending}
            data-testid="author-review-approve"
          >
            Approve
          </Button>
          <Button
            variant="secondary"
            onClick={() => setReopening(true)}
            disabled={review.isPending}
            data-testid="author-review-reopen"
          >
            Reopen
          </Button>
        </div>
      ) : (
        <div className="space-y-2">
          <label
            htmlFor="author-review-reason"
            className="block text-xs font-medium text-ms-text-secondary"
          >
            Reason (required)
          </label>
          <textarea
            id="author-review-reason"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            maxLength={REASON_MAX}
            rows={3}
            className="w-full text-sm px-2 py-1.5 border border-ms-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-400"
            placeholder="Explain what's still wrong…"
            data-testid="author-review-reason"
          />
          <div className="flex items-center gap-2">
            <Button
              variant="primary"
              onClick={() => submit('reopened', reasonTrimmed)}
              disabled={!canSubmitReopen}
              data-testid="author-review-reopen-submit"
            >
              Submit
            </Button>
            <Button
              variant="secondary"
              onClick={() => {
                setReopening(false);
                setReason('');
              }}
              disabled={review.isPending}
            >
              Cancel
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
